import { Config } from "../../model_gen/config";
import {
  ChatCompletionRequest,
  ChatCompletionParams1,
  ChatMessage,
  FallbackConfig as ChatFallbackConfig,
} from "../../model_gen/chat";
import { InferenceLocations } from "../../types/IConfig";

function parseInferenceLocation(
  req: ChatCompletionRequest
): InferenceLocations {
  const il = req.inference_location;
  if (il === "CLIENT" || il === "0" || (il as unknown) === 0) {
    return InferenceLocations.CLIENT;
  }
  return InferenceLocations.SERVER;
}

export function buildConfigFromChatRequest(req: ChatCompletionRequest): Config {
  const p = req.params;
  return {
    inference_location: parseInferenceLocation(req),
    evaluation_enabled: req.evaluation_enabled ?? false,
    apply_corrections: req.apply_corrections ?? false,
    safe_mode: req.safe_mode ?? false,
    model: p?.model ?? undefined,
    temperature: p?.temperature != null ? p.temperature : 1,
    max_tokens: p?.max_tokens ?? null,
    n: p?.n != null ? p.n : 1,
    presence_penalty: p?.presence_penalty != null ? p.presence_penalty : 0,
    frequency_penalty: p?.frequency_penalty != null ? p.frequency_penalty : 0,
    top_p: p?.top_p ?? null,
    top_k: p?.top_k ?? null,
    min_p: p?.min_p ?? null,
    repetition_penalty: p?.repetition_penalty ?? null,
    stop: p?.stop ?? null,
    logprobs: p?.logprobs ?? false,
    reasoning_effort: p?.reasoning_effort ?? undefined,
    streaming: p?.stream ?? false,
    fallback_model: req.fallback_model ?? null,
    fallback_config:
      (req.fallback_config ?? null) as Config["fallback_config"],
    extract_request_metadata: false,
  };
}

export function mergeConfigPatchIntoRequest(
  base: ChatCompletionRequest,
  patch: Partial<Config>
): ChatCompletionRequest {
  const next: ChatCompletionRequest = {
    ...base,
    params: {
      ...base.params,
      messages: base.params?.messages ? [...base.params.messages] : [],
    } as ChatCompletionParams1,
  };

  if (patch.model !== undefined) {
    next.params.model = patch.model;
  }
  if (patch.temperature !== undefined) {
    next.params.temperature = patch.temperature;
  }
  if (patch.max_tokens !== undefined) {
    next.params.max_tokens = patch.max_tokens;
  }
  if (patch.n !== undefined) {
    next.params.n = patch.n;
  }
  if (patch.presence_penalty !== undefined) {
    next.params.presence_penalty = patch.presence_penalty;
  }
  if (patch.frequency_penalty !== undefined) {
    next.params.frequency_penalty = patch.frequency_penalty;
  }
  if (patch.top_p !== undefined) {
    next.params.top_p = patch.top_p;
  }
  if (patch.top_k !== undefined) {
    next.params.top_k = patch.top_k;
  }
  if (patch.min_p !== undefined) {
    next.params.min_p = patch.min_p;
  }
  if (patch.repetition_penalty !== undefined) {
    next.params.repetition_penalty = patch.repetition_penalty;
  }
  if (patch.stop !== undefined) {
    next.params.stop = patch.stop;
  }
  if (patch.logprobs !== undefined) {
    next.params.logprobs = patch.logprobs;
  }
  if (patch.reasoning_effort !== undefined) {
    next.params.reasoning_effort = patch.reasoning_effort;
  }
  if (patch.streaming !== undefined) {
    next.params.stream = patch.streaming;
  }

  if (patch.inference_location !== undefined) {
    next.inference_location =
      patch.inference_location === InferenceLocations.CLIENT
        ? "CLIENT"
        : "SERVER";
  }
  if (patch.evaluation_enabled !== undefined) {
    next.evaluation_enabled = patch.evaluation_enabled;
  }
  if (patch.apply_corrections !== undefined) {
    next.apply_corrections = patch.apply_corrections;
  }
  if (patch.safe_mode !== undefined) {
    next.safe_mode = patch.safe_mode;
  }
  if (patch.fallback_model !== undefined) {
    next.fallback_model = patch.fallback_model;
  }
  if (patch.fallback_config !== undefined) {
    next.fallback_config = patch.fallback_config as ChatFallbackConfig;
  }

  return next;
}

export function mergeRerunRequestWithConfigPatch(
  originalRequest: ChatCompletionRequest,
  messages: ChatMessage[],
  configPatch: Partial<Config>
): ChatCompletionRequest {
  const base: ChatCompletionRequest = {
    ...originalRequest,
    params: {
      ...originalRequest.params,
      messages,
    },
  };
  return mergeConfigPatchIntoRequest(base, configPatch);
}
